import { NavigationProp, useNavigation } from "@react-navigation/native";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import AgreementText from "components/AgreementText";
import Button from "components/Button";
import {
  handleOnSubmitEditing,
  RenderUserFields,
} from "components/NewMemberForm";
import PlanSelector from "components/PlanSelector";
import ProfileAvatar from "components/ProfileAvatar";
import RenderHtml from "components/RenderHtml";
import TextInput from "components/TextInput";
import { isDev } from "config/env";
import { LIGHT_BLUE, validationSchemas } from "constants/";
import {
  setPrimaryUser,
  setSelectedPlanPackage,
  setStep,
} from "features/slices/buyFlowSlice";
import { PrimaryUser } from "features/types";
import { Field, Formik } from "formik";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useTenantUserFields from "hooks/useTenantUserFields";
import { LoginFlowStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { trackConversion } from "queries/conversion.queries";
import React, { useMemo, useRef, useState } from "react";
import { Pressable, View } from "react-native";
import userServices from "services/user.services";
import { getEmailValidation, getPlanType } from "utils/planPackageUtils";
import toast from "utils/toast";
import { object, string as yupString } from "yup";
import { generateFakeUser } from "./helpers";
import { emailExistsInSecondaryUsers, styles } from "./registerUtils";

interface Props {
  createAccount: () => void;
}
const StepOne: React.FC<Props> = (props) => {
  const { createAccount } = props;
  const dispatch = useAppDispatch();
  const {
    primaryUser,
    secondaryUsers,
    selectedPlanPackage,
    planPackages,
    shouldCharge,
  } = useAppSelector((state) => state.buyFlow);

  const {
    tenantUserFields,
    tenantFieldsValidation,
    fieldRefs: tenantUserFieldRefs,
  } = useTenantUserFields();
  const {
    planPackageUserFields,
    planPackageFieldsValidation,
    fieldRefs: planPackageUserFieldsRefs,
  } = usePlanPackageUserFields(selectedPlanPackage?.id);
  const { mutateAsync: track } = useMutation(trackConversion);

  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const requirePlayerId = tenantConfig?.requirePlayerIdForSignup ?? false;

  const [emailError, setEmailError] = useState(false);
  const navigation = useNavigation<NavigationProp<LoginFlowStackParamList>>();
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const passwordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const playerIdRef = useRef(null);
  const { basicValidation, passwordValidation } = validationSchemas({
    emailValidation: getEmailValidation(selectedPlanPackage),
  });
  const playerIdValidation: Record<string, any> = requirePlayerId
    ? { playerId: yupString().required(i18n.t("errors.form.required")) }
    : {};

  const isBankType = selectedPlanPackage
    ? getPlanType(selectedPlanPackage) === "BANK"
    : false;

  const handleFormSubmit = async (values: PrimaryUser) => {
    // check if email exists in system
    const userExists = await userServices.userExists(values.email as string);

    if (userExists) {
      setEmailError(true);
      return;
    }

    // check if email exists in secondary users
    if (emailExistsInSecondaryUsers(values.email as string, secondaryUsers)) {
      toast.error(i18n.t("screens.Register.emailBeingUsedError"));
      return;
    }
    dispatch(setPrimaryUser(values));
    if (!shouldCharge && isBankType) {
      await track({ step: "PURCHASE_COMPLETE", flow: "REGISTER" });
      createAccount && createAccount();
    } else {
      await track({ step: "USER_INFO_ENTERED", flow: "REGISTER" });
      if (selectedPlanPackage && getPlanType(selectedPlanPackage) !== "BANK") {
        dispatch(setStep(2));
      } else {
        dispatch(setStep(3));
      }
    }
  };

  const buttonText = useMemo(() => {
    return i18n.t("screens.Register.agreeAndContinue");
  }, [shouldCharge, isBankType]);

  if (planPackages?.length === 0 || !selectedPlanPackage) {
    return (
      <View>
        <Text category="h6" style={{ textAlign: "center", marginBottom: 15 }}>
          {i18n.t("screens.Register.comingSoon")}
        </Text>
      </View>
    );
  }

  // brewers.[go|demo|stage].fanrally.com
  const isNotDemo = window.location.host.split(".")[1] !== "demo";

  return (
    <View>
      {/* 
      <Text category="h6">{i18n.t("screens.Register.primaryMember")}</Text>
      <Divider style={{ marginTop: 10, marginBottom: 20 }}></Divider> */}
      {planPackages ? (
        <PlanSelector
          onChange={(plan) => dispatch(setSelectedPlanPackage(plan))}
          selectedPlanPackage={selectedPlanPackage}
          planPackages={planPackages}
        />
      ) : (
        <Text category="h6">Loading...</Text>
      )}

      <View className="my-4">
        <RenderHtml source={selectedPlanPackage?.description || ""} />
      </View>

      <Formik
        initialValues={primaryUser}
        onSubmit={handleFormSubmit}
        validationSchema={object({
          ...basicValidation,
          ...passwordValidation,
          ...playerIdValidation,
          ...tenantFieldsValidation,
          ...planPackageFieldsValidation,
        })}
        validateOnMount={false}
      >
        {({
          handleSubmit,
          values,
          setFieldValue,
          isValid,
          setFieldTouched,
        }) => {
          return (
            <View>
              {isDev && isNotDemo && (
                <Button
                  onPress={() => {
                    generateFakeUser(setFieldValue, setFieldTouched);
                  }}
                  style={{ alignSelf: "flex-start", marginVertical: 10 }}
                >
                  Fill Form
                </Button>
              )}
              <View style={{ alignItems: "center", marginBottom: 15 }}>
                <ProfileAvatar
                  editButton
                  hasImageIdentifier={values.imageIdentifier !== ""}
                  size={96}
                  source={{ uri: values.imageIdentifier }}
                  onChange={(uri) => setFieldValue("imageIdentifier", uri)}
                />
                {/* <Text category="c1">
                  {i18n.t("screens.Register.addMemberPhoto")}
                </Text> */}
              </View>
              <View className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                <Field
                  component={TextInput}
                  name="firstName"
                  label="First Name"
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(lastNameRef.current)
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(lastNameRef.current, e)
                  }
                  returnKeyLabel="next"
                />
                <Field
                  component={TextInput}
                  name="lastName"
                  label="Last Name"
                  innerRef={(ref: any) => {
                    lastNameRef.current = ref;
                  }}
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(emailRef.current)
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(emailRef.current, e)
                  }
                />
              </View>
              <Field
                component={TextInput}
                name="email"
                label="Email"
                innerRef={(ref: any) => {
                  emailRef.current = ref;
                }}
                onSubmitEditing={() =>
                  handleOnSubmitEditing(passwordRef.current)
                }
                onKeyPress={(e: any) =>
                  handleOnSubmitEditing(passwordRef.current, e)
                }
              />
              {emailError && (
                <Text style={styles.errorText}>
                  This email already exists.{" "}
                  <Pressable onPress={() => navigation.navigate("Login")}>
                    <Text style={{ color: LIGHT_BLUE, fontSize: 13 }}>
                      Please log in
                    </Text>
                  </Pressable>{" "}
                  using your existing account or use another email.
                </Text>
              )}
              <View className="grid grid-cols-1 md:grid-cols-2 md:gap-4">
                <Field
                  component={TextInput}
                  name="password"
                  label="Password"
                  secureTextEntry
                  innerRef={(ref: any) => {
                    passwordRef.current = ref;
                  }}
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(confirmPasswordRef.current)
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(confirmPasswordRef.current, e)
                  }
                />
                <Field
                  component={TextInput}
                  name="confirmPassword"
                  label="Confirm Password"
                  secureTextEntry
                  innerRef={(ref: any) => {
                    confirmPasswordRef.current = ref;
                  }}
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(phoneRef.current)
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(phoneRef.current, e)
                  }
                />
              </View>
              <Field
                component={TextInput}
                name="phone"
                label="Phone"
                innerRef={(ref: any) => {
                  phoneRef.current = ref;
                }}
                onSubmitEditing={() =>
                  handleOnSubmitEditing(
                    requirePlayerId
                      ? playerIdRef.current
                      : tenantUserFieldRefs.current[0]
                  )
                }
                onKeyPress={(e: any) =>
                  handleOnSubmitEditing(
                    requirePlayerId
                      ? playerIdRef.current
                      : tenantUserFieldRefs.current[0],
                    e
                  )
                }
              ></Field>

              {requirePlayerId && (
                <Field
                  component={TextInput}
                  name="playerId"
                  label="Player ID"
                  innerRef={(ref: any) => {
                    playerIdRef.current = ref;
                  }}
                  onSubmitEditing={() =>
                    handleOnSubmitEditing(tenantUserFieldRefs.current[0])
                  }
                  onKeyPress={(e: any) =>
                    handleOnSubmitEditing(tenantUserFieldRefs.current[0], e)
                  }
                />
              )}

              {tenantUserFields?.map((tenantUserField, i) => {
                const {
                  id,
                  field: { name, type },
                } = tenantUserField;

                return (
                  <RenderUserFields
                    key={id}
                    name={name}
                    type={type}
                    userField={tenantUserField}
                    userFieldRefs={tenantUserFieldRefs}
                    nextUserFieldRefs={planPackageUserFieldsRefs}
                    index={i}
                  />
                );
              })}
              {planPackageUserFields?.map((planPackageUserField, i) => {
                const {
                  id,
                  field: { name, type },
                } = planPackageUserField;

                return (
                  <RenderUserFields
                    key={id}
                    name={name}
                    type={type}
                    userField={planPackageUserField}
                    userFieldRefs={planPackageUserFieldsRefs}
                    index={i}
                  />
                );
              })}
              <View style={{ marginTop: 35, marginBottom: 15 }}>
                <Button
                  size="large"
                  disabled={!isValid}
                  onPress={() => handleSubmit()}
                  testID="continue-button"
                >
                  {buttonText}
                </Button>
              </View>

              <AgreementText
                btnText={buttonText}
                style={{ marginVertical: 10 }}
              />
            </View>
          );
        }}
      </Formik>
    </View>
  );
};

export default StepOne;
